import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Infinite brand strip: duplicated sequences, scroll-linked loop, optional auto-advance,
 * arrow previous/next, pointer drag on desktop.
 * Cell widths use ResizeObserver — 100cqw in scroll containers is unreliable on mobile Safari.
 * Auto-advance pauses when off-screen / tab hidden; coarse pointers write scroll less often.
 * Slow theme “Loop duration” values accumulate sub-pixel motion so long durations don’t stall.
 *
 * @extends {Component<{ viewport: HTMLElement }>}
 */
class BrandCarouselMarqueeComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {number | undefined} */
  #raf = undefined;

  /** @type {number} */
  #lastTs = 0;

  /** @type {boolean} */
  #dragging = false;

  /** @type {number} */
  #dragMoved = 0;

  /** @type {HTMLAnchorElement | null} */
  #dragLink = null;

  /** @type {number} */
  #dragLastX = 0;

  /** @type {(() => void) | undefined} */
  #arrowButtonCleanup;

  /** Pause auto-tick briefly so programmatic scroll (arrows) is not overwritten every frame */
  #suspendAutoUntil = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {boolean} */
  #resizeRafPending = false;

  /** @type {MediaQueryList | undefined} */
  #breakpointMql;

  /** Pause auto-advance when tab is hidden or carousel is off-screen (saves main thread / battery). */
  #documentVisible = true;

  /** @type {boolean} */
  #inView = true;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** Coarse pointers: half as many scroll writes (rAF still runs for scheduling). */
  #coarseFrameToggle = false;

  /**
   * Auto-scroll waits for the window load event: the per-frame scrollLeft writes
   * force layout every frame, and during page load that main-thread churn
   * competes with hero/LCP paint (PSI attributed ~3.3s CPU to this file).
   * Arrows and drag work immediately; only the ambient motion is delayed.
   */
  #autoStartReady = false;

  /** Sub-pixel remainder for auto-scroll so slow loop durations (large s) don’t stall after rounding. */
  #autoScrollCarry = 0;

  /** Ignore wrap/snap logic while native smooth scroll runs (otherwise #onScroll fights the animator). */
  #programmaticScrolling = false;

  /** Fallback timeout: clears smooth-arrow guard (avoid `scrollend` — teleport can trigger it early). */
  /** @type {number | undefined} */
  #programmaticScrollFallback;

  connectedCallback() {
    super.connectedCallback();

    const viewport = this.refs.viewport;
    viewport.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#syncMarqueeLayout();
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#resizeRafPending) return;
      this.#resizeRafPending = true;
      requestAnimationFrame(() => {
        this.#resizeRafPending = false;
        this.#syncMarqueeLayout();
      });
    });
    this.#resizeObserver.observe(viewport);

    this.#breakpointMql = window.matchMedia('(min-width: 750px)');
    if (typeof this.#breakpointMql.addEventListener === 'function') {
      this.#breakpointMql.addEventListener('change', this.#syncMarqueeLayout);
    } else {
      this.#breakpointMql.addListener(this.#syncMarqueeLayout);
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.target === this) {
            this.#inView = e.isIntersecting;
            this.#syncTickRunning();
            break;
          }
        }
      },
      { root: null, rootMargin: '80px 0px', threshold: 0 },
    );
    this.#intersectionObserver.observe(this);

    document.addEventListener('visibilitychange', this.#onDocumentVisibility);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      this.#lastTs = performance.now();
      if (document.readyState === 'complete') {
        this.#autoStartReady = true;
        this.#syncTickRunning();
      } else {
        window.addEventListener('load', this.#onWindowLoad, { once: true });
      }
    }

    viewport.addEventListener('pointerdown', this.#onPointerDown, { capture: true });

    queueMicrotask(() => {
      this.#syncMarqueeLayout();
      this.#bindArrowButtons();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('load', this.#onWindowLoad);
    this.#unbindArrowButtons();
    this.#stopTick();
    const viewport = this.refs.viewport;
    viewport?.removeEventListener('scroll', this.#onScroll);
    window.clearTimeout(this.#programmaticScrollFallback);
    viewport?.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('visibilitychange', this.#onDocumentVisibility);
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    if (this.#breakpointMql) {
      if (typeof this.#breakpointMql.removeEventListener === 'function') {
        this.#breakpointMql.removeEventListener('change', this.#syncMarqueeLayout);
      } else {
        this.#breakpointMql.removeListener(this.#syncMarqueeLayout);
      }
      this.#breakpointMql = undefined;
    }
  }

  #onDocumentVisibility = () => {
    this.#documentVisible = document.visibilityState === 'visible';
    this.#syncTickRunning();
  };

  #onWindowLoad = () => {
    // A further 3s clear of `load` keeps the per-frame layout writes out of the
    // LCP window even when `load` fires before the hero paints (throttled mobile).
    window.setTimeout(() => {
      this.#autoStartReady = true;
      this.#lastTs = performance.now();
      this.#syncTickRunning();
    }, 3000);
  };

  /** @returns {boolean} */
  #coarsePointer() {
    return (
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
    );
  }

  #stopTick() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = undefined;
  }

  #syncTickRunning() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldRun =
      !reduced && this.#autoStartReady && this.isConnected && this.#documentVisible && this.#inView;

    if (shouldRun) {
      if (this.#raf == null) {
        this.#lastTs = performance.now();
        this.#raf = requestAnimationFrame(this.#tick);
      }
    } else {
      this.#stopTick();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    queueMicrotask(() => {
      this.#syncMarqueeLayout();
      this.#bindArrowButtons();
    });
  }

  /** Sets --marquee-cell-width from viewport clientWidth (avoids fragile 100cqw in overflow scroll). */
  #syncMarqueeLayout = () => {
    const viewport = this.refs.viewport;
    if (!viewport || !this.isConnected) return;

    const w = viewport.clientWidth;
    if (w < 8) return;

    const desktop =
      typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 750px)').matches;
    const raw = desktop ? this.dataset.brandsDesktop : this.dataset.brandsMobile;
    let n = Number.parseInt(String(raw ?? ''), 10);
    if (!Number.isFinite(n) || n < 1) n = desktop ? 6 : 2;

    const gapStr = getComputedStyle(viewport).getPropertyValue('--slideshow-gap').trim();
    const gap = Number.parseFloat(gapStr) || 16;
    const totalGaps = Math.max(0, n - 1) * gap;
    const cell = (w - totalGaps) / n;
    const px = Math.max(40, Math.round(cell * 100) / 100);
    viewport.style.setProperty('--marquee-cell-width', `${px}px`);
  };

  /**
   * Slideshow arrow buttons: bind directly and drop `on:click` so document delegation doesn’t double-fire.
   */
  #bindArrowButtons() {
    this.#unbindArrowButtons();
    const root = this.querySelector('slideshow-arrows');
    if (!root) return;

    const prev = root.querySelector('.slideshow-control--previous');
    const next = root.querySelector('.slideshow-control--next');

    const onPrev = (/** @type {Event} */ e) => {
      e.preventDefault();
      e.stopPropagation();
      this.previous(e);
    };
    const onNext = (/** @type {Event} */ e) => {
      e.preventDefault();
      e.stopPropagation();
      this.next(e);
    };

    for (const btn of root.querySelectorAll('.slideshow-control')) {
      if (btn instanceof HTMLButtonElement) btn.removeAttribute('on:click');
    }

    prev?.addEventListener('click', onPrev, { capture: true });
    next?.addEventListener('click', onNext, { capture: true });

    this.#arrowButtonCleanup = () => {
      prev?.removeEventListener('click', onPrev, { capture: true });
      next?.removeEventListener('click', onNext, { capture: true });
    };
  }

  #unbindArrowButtons() {
    this.#arrowButtonCleanup?.();
    this.#arrowButtonCleanup = undefined;
  }

  /**
   * @param {number} ms
   */
  #suspendAuto(ms) {
    this.#suspendAutoUntil = performance.now() + ms;
  }

  #finalizeProgrammaticScroll() {
    window.clearTimeout(this.#programmaticScrollFallback);
    this.#programmaticScrollFallback = undefined;
    if (!this.#programmaticScrolling) return;
    this.#programmaticScrolling = false;
    this.#onScroll();
  }

  /**
   * Arrows use native `scrollBy({ behavior: 'smooth' })`; defer wrap/re-round (#onScroll) until the
   * animation finishes so we do not shorten the glide.
   */
  #beginArrowSmoothScroll() {
    this.#autoScrollCarry = 0;
    this.#suspendAuto(4200);
    window.clearTimeout(this.#programmaticScrollFallback);
    this.#programmaticScrolling = true;

    this.#programmaticScrollFallback = window.setTimeout(() => {
      this.#finalizeProgrammaticScroll();
    }, 3600);
  }

  /**
   * Before scrolling left by ~one viewport: if we'd clamp at 0, jump into duplicated half first.
   * The old `scrollLeft < step − 1` check missed `step − 1` / float cases and clipped the swipe.
   * @param {HTMLElement} v
   * @param {number} half
   * @param {number} step
   */
  #ensureBackwardTeleport(v, half, step) {
    if (half <= 0 || !(step > 0)) return;
    if (v.scrollLeft + 0.51 >= step) return;
    v.scrollLeft += half;
  }

  /**
   * Mirror of `#ensureBackwardTeleport`: max scroll is finite, so repeated "next" clamps unless we
   * jump back one loop length (same visual strip, first copy).
   * @param {HTMLElement} v
   * @param {number} half
   * @param {number} step
   */
  #ensureForwardTeleport(v, half, step) {
    if (half <= 0 || !(step > 0)) return;
    const maxLeft = Math.max(0, v.scrollWidth - v.clientWidth);
    if (v.scrollLeft + step <= maxLeft + 0.51) return;
    v.scrollLeft -= half;
  }

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    const v = this.refs.viewport;
    const half = v.scrollWidth / 2;
    const step = this.#stepSize();
    this.#ensureBackwardTeleport(v, half, step);

    const useSmooth = !prefersReducedMotion();

    if (useSmooth) {
      this.#beginArrowSmoothScroll();
      v.scrollBy({ left: -step, behavior: 'smooth' });
    } else {
      this.#suspendAuto(450);
      v.scrollLeft -= step;
      this.#onScroll();
    }
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    const v = this.refs.viewport;
    const half = v.scrollWidth / 2;
    const step = this.#stepSize();
    this.#ensureForwardTeleport(v, half, step);
    const useSmooth = !prefersReducedMotion();

    if (useSmooth) {
      this.#beginArrowSmoothScroll();
      v.scrollBy({ left: step, behavior: 'smooth' });
    } else {
      this.#suspendAuto(450);
      v.scrollLeft += step;
      this.#onScroll();
    }
  }

  #stepSize() {
    const v = this.refs.viewport;
    return Math.max(120, Math.floor(v.clientWidth * 0.85));
  }

  #onScroll = () => {
    if (this.#programmaticScrolling) return;

    const v = this.refs.viewport;
    const half = v.scrollWidth / 2;
    if (half <= 0) return;
    let left = v.scrollLeft;
    let guard = 0;
    while (left >= half - 0.5 && guard < 24) {
      left -= half;
      guard += 1;
    }
    const rounded = Math.round(left);
    if (rounded !== v.scrollLeft) {
      v.scrollLeft = rounded;
    }
  };

  /**
   * @param {number} ts
   */
  #tick = (ts) => {
    this.#raf = undefined;
    if (!this.isConnected || !this.#documentVisible || !this.#inView) return;

    const v = this.refs.viewport;
    let base = Number(this.dataset.loopDuration || '45') * 1000;
    if (this.#coarsePointer()) {
      base *= 1.35;
    }
    const duration = base;
    const half = v.scrollWidth / 2;

    const autoSuspended = performance.now() < this.#suspendAutoUntil;

    if (half > 0 && duration > 0 && !this.#dragging && !autoSuspended) {
      const coarse = this.#coarsePointer();
      let skipWrite = false;
      if (coarse) {
        this.#coarseFrameToggle = !this.#coarseFrameToggle;
        skipWrite = this.#coarseFrameToggle;
      }

      const dt = ts - this.#lastTs;
      this.#lastTs = ts;

      // Ideal px advance this frame: (loop half-width) / (loop duration in ms).
      this.#autoScrollCarry += (half / duration) * dt;

      if (!skipWrite) {
        const step = Math.trunc(this.#autoScrollCarry);
        this.#autoScrollCarry -= step;
        if (step !== 0) {
          v.scrollLeft += step;
          this.#onScroll();
        }
      }
    } else {
      this.#lastTs = ts;
    }

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !this.#documentVisible ||
      !this.#inView
    ) {
      return;
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType === 'touch') return;
    if (event.target.closest('button, input, textarea, [role="button"]')) return;

    this.#dragging = true;
    this.#dragMoved = 0;
    this.#dragLink = event.target.closest('a[href]');
    this.#dragLastX = event.clientX;
    const viewport = this.refs.viewport;
    viewport.setPointerCapture(event.pointerId);
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    document.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    document.addEventListener('pointercancel', this.#onPointerUp, { passive: true });
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    const dx = event.clientX - this.#dragLastX;
    this.#dragLastX = event.clientX;
    this.#dragMoved += Math.abs(dx);
    const v = this.refs.viewport;
    v.scrollLeft -= dx;
    this.#onScroll();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    const moved = this.#dragMoved;
    const link = this.#dragLink;
    this.#dragging = false;
    this.#dragMoved = 0;
    this.#dragLink = null;
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('pointercancel', this.#onPointerUp);
    try {
      if (this.refs.viewport.hasPointerCapture(event.pointerId)) {
        this.refs.viewport.releasePointerCapture(event.pointerId);
      }
    } catch {
      /* noop */
    }

    if (moved > 8 && link instanceof HTMLAnchorElement) {
      const anchor = link;
      const suppress = (/** @type {Event} */ clickEvent) => {
        const t = clickEvent.target;
        if (t instanceof Node && anchor.contains(t)) {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        }
      };
      anchor.addEventListener('click', suppress, { capture: true, once: true });
    }
  };
}

if (!customElements.get('brand-carousel-marquee-component')) {
  customElements.define('brand-carousel-marquee-component', BrandCarouselMarqueeComponent);
}
